/**
 * DS Compare — Verdict engine for externally-loaded products
 * ---------------------------------------------------------------
 * The section's own Analyze handler reads the competitor from a variable inside
 * its closure, which is only populated when IT renders a card (dropdown / its
 * own photo flow). Products we load from a link land outside that closure, so
 * clicking Analyze did nothing.
 *
 * Rather than fight the closure, this renders the verdict itself — same four
 * criteria, same markup, same "fair comparison" honesty — and runs it
 * automatically as soon as a card is painted.
 *
 * DS Labs v1.1: shampoo-category criteria with appearance-based (cosmetic)
 * claim language — density-support actives,
 * gentle sulfate-free cleansing, signal vs filler.
 */
(function () {
  var CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
  var EQUAL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M5 9h14M5 15h14"/></svg>';
  var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13M13 6l6 6-6 6"/></svg>';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function ownData() {
    var el = document.getElementById('dscmp-own');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return {}; }
  }

  function parseIng(raw) {
    return String(raw == null ? '' : raw).split(/\r?\n+/).filter(function (l) { return l.trim(); })
      .map(function (l) {
        var p = l.split('|');
        if (p.length >= 3) {
          var role = p[0].trim().toUpperCase();
          return { role: (role === 'ACTIVE' || role === 'SUPPORT' || role === 'FILLER') ? role : 'FILLER' };
        }
        return { role: 'FILLER' };
      });
  }

  function ownCounts() {
    var own = ownData();
    var a = 0, s = 0, f = 0;
    parseIng(own.benefits).forEach(function (i) {
      if (i.role === 'ACTIVE') a++; else if (i.role === 'SUPPORT') s++; else f++;
    });
    return { a: a, s: s, f: f };
  }

  /* Count the competitor's tiers straight off the rendered card, so this stays
     in step with however the card was built. */
  function compCounts() {
    var list = document.getElementById('dscmp-c-benefits');
    var a = 0, s = 0, f = 0;
    if (list) {
      a = list.querySelectorAll('.dscmp-ing--active').length;
      s = list.querySelectorAll('.dscmp-ing--support').length;
      f = list.querySelectorAll('.dscmp-ing--filler').length;
    }
    return { a: a, s: s, f: f };
  }

  function spec(key) {
    var el = document.querySelector('#dscmp-vscard [data-cspec="' + key + '"]');
    return el ? (el.textContent || '').trim() : '';
  }

  function compName() {
    var el = document.getElementById('dscmp-cname');
    if (!el) return 'that product';
    var clone = el.cloneNode(true);
    var tag = clone.querySelector('.dscmp-idtag');
    if (tag) tag.remove();
    return (clone.textContent || '').trim() || 'that product';
  }

  function isYes(v) { return /^\s*yes\b/i.test(String(v || '')); }

  function build(own, comp) {
    var oc = ownCounts(), cc = compCounts();
    var oName = String(own.name || 'This product');
    var cName = comp.name;

    var oSulf = isYes(own.sulfate), cSulf = isYes(comp.sulfate);

    var reasons = [];

    if (oc.a > cc.a) {
      reasons.push({ k: 'win', t: 'Density-support actives', s: oName + ' lists ' + oc.a + ' density-support actives vs ' + cc.a + ' — more ingredients working toward the appearance of thicker, fuller-looking hair.' });
    } else if (oc.a === cc.a) {
      reasons.push({ k: 'tie', t: 'Density-support actives', s: 'Both list ' + oc.a + ' density-support active(s).' });
    } else {
      reasons.push({ k: 'lose', t: 'Density-support actives', s: cName + ' lists more density-support actives in this comparison.' });
    }

    if (oSulf && !cSulf) {
      reasons.push({ k: 'win', t: 'Gentle cleansing', s: oName + ' is sulfate-free — it cleans without the harsh detergents linked to dryness and breakage-prone strands.' });
    } else if (oSulf && cSulf) {
      reasons.push({ k: 'tie', t: 'Gentle cleansing', s: 'Both are sulfate-free.' });
    } else {
      reasons.push({ k: 'tie', t: 'Gentle cleansing', s: 'Sulfate content is comparable or not stated, so we can’t call this one.' });
    }

    if (cc.f > oc.f) {
      reasons.push({ k: 'win', t: 'Signal vs filler', s: oName + ' is ' + oc.a + ' active to ' + oc.f + ' filler; ' + cName + ' is ' + cc.a + ' active to ' + cc.f + ' filler.' });
    } else {
      reasons.push({ k: 'tie', t: 'Signal vs filler', s: 'Active-to-filler ratios are comparable.' });
    }

    var wins = reasons.filter(function (r) { return r.k === 'win'; }).length;
    var loses = reasons.filter(function (r) { return r.k === 'lose'; }).length;

    var headline, sub, fair;

    if (wins > loses) {
      headline = 'Better for hair density: ' + oName;
      sub = 'Based on the attributes compared here, ' + oName + ' covers more of what supports the appearance of hair density — density-support actives and gentle sulfate-free cleansing.';
    } else if (wins === loses) {
      headline = 'Closely matched — it comes down to preference';
      sub = cName + ' covers similar ground on paper. The practical differences are the cleansing base, the ingredient lineup, and how it feels in daily use.';
    } else {
      headline = cName + ' lists broader coverage';
      sub = 'On the attributes compared here, ' + cName + ' lists more of the ingredients associated with the appearance of density than ' + oName + '.';
    }

    /* Honest concession — name where the other product genuinely wins. */
    fair = cName + ' is a legitimate option. The difference is in the number of density-support actives and the cleansing base.';

    var rows = reasons.map(function (r) {
      var icon = r.k === 'win' ? CHECK : (r.k === 'lose' ? ARROW : EQUAL);
      return '<div class="dscmp-reason dscmp-reason--' + r.k + '">' +
        '<span class="dscmp-rico">' + icon + '</span>' +
        '<span class="dscmp-reason__body"><b>' + esc(r.t) + '</b><span>' + esc(r.s) + '</span></span>' +
        '</div>';
    }).join('');

    return '<div class="dscmp-rec"><span class="dscmp-rec__badge">' + CHECK + '</span>' +
      '<div class="dscmp-rec__body"><h4>' + esc(headline) + '</h4><p>' + esc(sub) + '</p></div></div>' +
      '<div class="dscmp-reasons">' + rows + '</div>' +
      '<div class="dscmp-fair"><span class="dscmp-fair__icon">i</span>' +
      '<span><b>Fair comparison</b>' + esc(fair) + '</span></div>' +
      '<a class="dscmp-cta" href="#" id="dscmp-cta">Choose ' + esc(oName) + '</a>';
  }

  /* Public: called by the add-ons script after it paints an external card. */
  window.DSCMP_VERDICT = function () {
    var card = document.getElementById('dscmp-vscard');
    var verdict = document.getElementById('dscmp-verdict');
    var summary = document.getElementById('dscmp-summary');
    var body = document.getElementById('dscmp-summary-body');
    if (!card || !verdict || card.classList.contains('is-empty')) return;

    var comp = {
      name: compName(),
      sulfate: spec('sulfate'),
      format: spec('format'),
      key_active: spec('key_active')
    };

    verdict.innerHTML = build(ownData(), comp);
    verdict.hidden = false;

    if (body) {
      body.innerHTML = 'Comparing <b>' + esc(ownData().name || 'this product') + '</b> with <b>' + esc(comp.name) + '</b>.';
    }

    var analyze = document.getElementById('dscmp-analyze');
    if (analyze) analyze.disabled = false;

    if (summary) {
      summary.classList.remove('is-flash');
      void summary.offsetWidth;
      summary.classList.add('is-flash');
    }
  };
})();
